import React, { useState, useEffect, useMemo } from 'react';
import { Loading, ErrorBox, HowCalculated, fmtMins } from '../MetricsUI';
import { windowBody } from '../cohort/CohortFilters';
import AdminFilters, { EMPTY_ADMIN_FILTERS, applyAdminFilters } from './AdminFilters';
import Portfolio from './Portfolio';
import Accounts from './Accounts';
import Efficacy from './Efficacy';
import AdminContent from './AdminContent';
import { fetchAdminOverview, fetchAdminContent } from '../../../apis/metrics.api';

/*
 * The admin console: every organisation at once, with the same drill-down the
 * manager page already provides.
 *
 * Two fetches, split the way the endpoints are: the overview moves with the date
 * range, the content view is about the catalogue and only reloads with it because
 * usage counts sit inside it.
 */

const TABS = [
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'efficacy', label: 'Efficacy' },
  { key: 'content', label: 'Content' },
];

export default function AdminConsole({ idToken, viewer, onOpenOrg }) {
  const [tab, setTab] = useState('portfolio');
  const [filters, setFilters] = useState(EMPTY_ADMIN_FILTERS);
  const [overview, setOverview] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);

  /*
   * Memoised on the SELECTION, not recomputed per render. windowBody reads
   * Date.now(), so calling it inline gives a different window every render; as an
   * effect dependency that is an infinite fetch loop. This is the same mistake
   * that showed up as the manager page flickering.
   */
  const { range, from: customFrom, to: customTo, regions: regionSel } = filters;
  /* Joined into a string so the memo compares by value: a fresh array literal
     every render would make this an infinite fetch loop, which is the same
     mistake that showed up as the manager page flickering. */
  const regionKey = (regionSel || []).slice().sort().join(',');
  /* A stable identity for the current window + region selection, so data can
     say which one it was fetched for. */
  const winKey = `${range}|${customFrom}|${customTo}|${regionKey}`;
  const win = useMemo(
    () => {
      const w = windowBody({ range, from: customFrom, to: customTo });
      if (!w) return null;
      return regionKey ? { ...w, regions: regionKey.split(',') } : w;
    },
    [range, customFrom, customTo, regionKey]
  );

  /*
   * Refetch WITHOUT clearing what is on screen.
   *
   * Nulling the overview first replaced the console with a spinner, and the new
   * data remounted every Section — which reset each one to collapsed. Changing
   * the date range or a region threw away the detail you had open, on the way
   * out and again on the way back.
   *
   * Keeping the previous overview means nothing unmounts, so open sections stay
   * open, and `refreshing` drives a quiet indicator instead. Only the first load
   * has nothing to show.
   */
  useEffect(() => {
    if (!win) return undefined;
    let cancelled = false;
    setRefreshing(true);
    setError(null);
    fetchAdminOverview(idToken, win)
      .then((d) => {
        if (cancelled) return;
        setOverview(d);
        setRefreshing(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, win]);

  /*
   * Content is fetched lazily, the first time the tab is opened. It is the most
   * expensive query on the page — every attempt on the platform, grouped by
   * module — and most visits to this console never open it.
   */
  useEffect(() => {
    if (tab !== 'content' || !win || content) return undefined;
    let cancelled = false;
    fetchAdminContent(idToken, win)
      .then((d) => !cancelled && setContent({ ...d, __win: winKey }))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
    // `content` is deliberately not a dependency beyond the guard above: adding
    // it would refetch immediately after it arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, win, tab]);

  /*
   * The content view is refetched when the range changes, in place.
   *
   * It used to be cleared to null, which unmounted the Content tab's section
   * and collapsed it — the same problem as the overview, one tab over. A stamp
   * of the window it was fetched for is enough to know it is stale, so the old
   * figures stay on screen until the new ones land.
   */
  const contentStale = content && content.__win !== winKey;
  useEffect(() => {
    if (tab !== 'content' || !win || !contentStale) return undefined;
    let cancelled = false;
    setRefreshing(true);
    fetchAdminContent(idToken, win)
      .then((d) => {
        if (cancelled) return;
        setContent({ ...d, __win: winKey });
        setRefreshing(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, win, winKey, tab, contentStale]);

  const orgsById = useMemo(
    () => new Map((overview ? overview.organizations : []).map((o) => [o.organizationId, o])),
    [overview]
  );

  /*
   * Name and size narrow the rows already in hand, so they respond instantly
   * and the counts beside them stay exact.
   *
   * Region is NOT applied here — it goes to the server, because it changes the
   * summary tiles and the efficacy histogram too, and a filtered table under
   * platform-wide tiles is two answers on one screen.
   *
   * Every tab reads `view`, so a search is applied everywhere rather than only
   * on the table the box happens to sit above.
   */
  const view = useMemo(() => {
    if (!overview) return null;
    const organizations = applyAdminFilters(overview.organizations, {
      ...filters,
      regions: [],
    });
    return { ...overview, organizations };
  }, [overview, filters]);

  if (error) return <ErrorBox message={error} />;

  const header = <PlatformHeader overview={overview} viewer={viewer} />;

  if (!overview) {
    return (
      <div className="space-y-8">
        {header}
        <AdminFilters filters={filters} onChange={setFilters} organizations={[]} regions={[]} />
        {win ? (
          <Loading what="the platform" />
        ) : (
          <p className="text-sm italic text-gray-500">Choose a start and end date to load the platform.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {header}

      <AdminFilters
        filters={filters}
        onChange={setFilters}
        organizations={overview.organizations}
        regions={overview.regions || []}
        regionTotals={overview.regionTotals || []}
      />

      {refreshing && (
        <p className="-mt-4 flex items-center gap-2 text-xs text-gray-500">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lavender" />
          Updating for the new selection — the figures below are the previous ones until it arrives.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              tab === t.key ? 'bg-navy text-white' : 'bg-white text-gray-600 shadow-sm hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'portfolio' && <Portfolio data={view} onOpenOrg={onOpenOrg} />}
      {tab === 'accounts' && <Accounts data={view} onOpenOrg={onOpenOrg} />}
      {tab === 'efficacy' && <Efficacy data={view} onOpenOrg={onOpenOrg} />}
      {tab === 'content' &&
        (content ? (
          <AdminContent data={content} orgsById={orgsById} onOpenOrg={onOpenOrg} />
        ) : (
          <Loading what="content" />
        ))}
    </div>
  );
}

function PlatformHeader({ overview, viewer }) {
  const p = overview ? overview.portfolio : null;
  const ROLE = { admin: 'Admin', leader: 'Manager', trainee: 'Trainee' };

  return (
    <div className="rounded-lg bg-navy p-6 text-white shadow">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-indigo-200">LavenirAI platform</p>
          <h1 className="text-2xl font-bold leading-tight">Admin console</h1>
          {p && (
            <p className="mt-0.5 text-sm text-indigo-200">
              {p.orgs} organisations · {p.members} people · {p.negotiations} case studies run ·{' '}
              {fmtMins(p.totalMs)} in the app
            </p>
          )}
        </div>

        {viewer && (
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-indigo-200">Signed in as</p>
            <p className="font-semibold">{viewer.name || viewer.email}</p>
            <p className="text-sm text-indigo-200">{ROLE[viewer.role] || viewer.role}</p>
          </div>
        )}
      </div>

      {p && (
        <div className="mt-4 flex flex-wrap gap-6 border-t border-white/10 pt-4 text-sm">
          <Headline label="People who have started" value={`${p.everStarted} / ${p.members}`} />
          <Headline label="Active in last 30 days" value={p.activeLast30} />
          <Headline
            label="Seats on record"
            value={p.orgsWithSeats ? `${p.seats} across ${p.orgsWithSeats} orgs` : 'none'}
            help="Paid seats, from the subscription on record in the registry (features.maxUsers). Organisations without a subscription are not counted."
          />
        </div>
      )}
    </div>
  );
}

function Headline({ label, value, help }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-indigo-300">
        {label}
        {help && <HowCalculated>{help}</HowCalculated>}
      </p>
      <p className="text-lg font-bold leading-tight">{value}</p>
    </div>
  );
}
